import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  PlusIcon, 
  BookOpenIcon, 
  EyeIcon, 
  PencilIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  BellIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  UsersIcon,
  EllipsisHorizontalIcon
} from '@heroicons/react/24/outline'
import { StarIcon } from '@heroicons/react/24/solid'

// ... (保留 Course 和 CourseStats 接口定义)
interface Course {
  id: string
  title: string
  description: string
  subject: string
  expectedPricePerHour: number
  finalPricePerHour: number
  duration: number
  status: 'pending' | 'approved' | 'rejected'
  createdAt: string
  updatedAt?: string
  reviewNotes?: string
  courseType?: 'individual' | 'group'
  groupPricePerHour?: number
  minStudents?: number
  maxStudents?: number
  rating?: number
  studentCount?: number
  _count?: {
    bookings: number
  }
}

interface CourseStats {
  totalCourses: number
  approvedCourses: number
  pendingCourses: number
  rejectedCourses: number
  totalEarningsPotential: number
  averagePrice: number
  mostPopularSubject: string
}

const TeacherCourses = () => {
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  // ... (保留原有 state 和 effect)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const visibleCoursesRef = useRef<Set<string>>(new Set())
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<CourseStats>({
    totalCourses: 0,
    approvedCourses: 0,
    pendingCourses: 0,
    rejectedCourses: 0,
    totalEarningsPotential: 0,
    averagePrice: 0,
    mostPopularSubject: ''
  })
  const [readCourses, setReadCourses] = useState<Set<string>>(new Set())

  // ... (保留 fetchCourses, calculateStats, getCoursePrice 等核心逻辑)
  const getCoursePrice = (course: Course): number => {
    if (course.courseType === 'group' && course.groupPricePerHour) {
      const groupPrice = parseFloat(String(course.groupPricePerHour))
      if (!isNaN(groupPrice) && groupPrice > 0) return groupPrice
    }
    const finalPrice = parseFloat(String(course.finalPricePerHour))
    const expectedPrice = parseFloat(String(course.expectedPricePerHour))
    if (!isNaN(finalPrice) && finalPrice > 0) return finalPrice
    if (!isNaN(expectedPrice) && expectedPrice > 0) return expectedPrice
    return 0
  }

  useEffect(() => {
    fetchCourses()
  }, [])

  useEffect(() => {
    if (courses.length > 0) calculateStats()
  }, [courses])

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/courses/teacher/my-courses', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        // 使用后端返回的真实数据
        const courses = Array.isArray(data) ? data : (data.courses || [])
        const enrichedData = courses.map((c: Course) => ({
          ...c,
          rating: c.rating ?? 0,
          studentCount: c._count?.bookings || c.studentCount || 0
        }))
        setCourses(enrichedData)
      } else {
        setCourses([])
      }
    } catch (error) {
      console.error('获取课程失败:', error)
      setCourses([])
    } finally {
      setLoading(false)
    }
  }

  const calculateStats = () => {
     // ... (原有逻辑)
     const totalCourses = courses.length
     const approvedCourses = courses.filter(c => c.status === 'approved').length
     const pendingCourses = courses.filter(c => c.status === 'pending').length
     const rejectedCourses = courses.filter(c => c.status === 'rejected').length
     
     const totalEarningsPotential = courses
       .filter(c => c.status === 'approved')
       .reduce((sum, c) => sum + (getCoursePrice(c) * (parseFloat(String(c.duration)) || 1)), 0)
     
     const averagePrice = totalCourses > 0 
       ? courses.reduce((sum, c) => sum + getCoursePrice(c), 0) / totalCourses 
       : 0

     const subjectCounts = courses.reduce((acc, course) => {
        acc[course.subject] = (acc[course.subject] || 0) + 1
        return acc
      }, {} as Record<string, number>)
      
    const mostPopularSubject = Object.entries(subjectCounts)
        .sort(([,a], [,b]) => b - a)[0]?.[0] || ''

    setStats({
        totalCourses,
        approvedCourses,
        pendingCourses,
        rejectedCourses,
        totalEarningsPotential,
        averagePrice,
        mostPopularSubject
    })
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      {/* 头部操作栏 */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">课程管理</h1>
          <p className="text-gray-500 mt-1">管理您的课程内容、定价和发布状态</p>
        </div>
        <div className="flex gap-3">
          <div className="flex bg-gray-100 p-1 rounded-lg">
            <button 
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'grid' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
            >
              卡片
            </button>
            <button 
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
            >
              列表
            </button>
          </div>
          <button
            onClick={() => navigate('/teacher/create-course')}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            发布新课程
          </button>
        </div>
      </div>

      {/* 核心指标 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <p className="text-xs text-gray-500 uppercase font-bold">已发布课程</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.approvedCourses}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <p className="text-xs text-gray-500 uppercase font-bold">平均单价</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">¥{Math.round(stats.averagePrice)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <p className="text-xs text-gray-500 uppercase font-bold">总课时数</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
                {courses.reduce((acc, c) => acc + (parseFloat(String(c.duration)) || 0), 0)}
            </p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <p className="text-xs text-gray-500 uppercase font-bold">平均评分</p>
            <div className="flex items-center mt-1">
                <span className="text-2xl font-bold text-gray-900">4.8</span>
                <div className="flex ml-2 text-yellow-400">
                    <StarIcon className="w-4 h-4" />
                    <StarIcon className="w-4 h-4" />
                    <StarIcon className="w-4 h-4" />
                    <StarIcon className="w-4 h-4" />
                    <StarIcon className="w-4 h-4" />
                </div>
            </div>
        </div>
      </div>

      {/* 课程展示 */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map(course => (
            <div key={course.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all group flex flex-col">
              <div className="p-6 flex-1">
                <div className="flex justify-between items-start mb-4">
                  <div className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                    course.status === 'approved' ? 'bg-green-50 text-green-700 border-green-100' :
                    course.status === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                    'bg-red-50 text-red-700 border-red-100'
                  }`}>
                    {course.status === 'approved' ? '已发布' : course.status === 'pending' ? '审核中' : '已拒绝'}
                  </div>
                  <button className="text-gray-400 hover:text-gray-600">
                    <EllipsisHorizontalIcon className="w-5 h-5" />
                  </button>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">
                  {course.title}
                </h3>
                <p className="text-sm text-gray-500 mb-4 line-clamp-2 h-10">
                  {course.description}
                </p>
                
                <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                  <div className="flex items-center">
                    <ClockIcon className="w-4 h-4 mr-1" />
                    {course.duration} 课时
                  </div>
                  <div className="flex items-center">
                    <UsersIcon className="w-4 h-4 mr-1" />
                    {course.studentCount || 0} 学员
                  </div>
                </div>
                
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-gray-900">¥{getCoursePrice(course)}</span>
                  <span className="text-xs text-gray-500">/课时</span>
                </div>
              </div>
              
              <div className="border-t border-gray-100 p-4 bg-gray-50 rounded-b-xl flex justify-between items-center">
                <div className="flex items-center text-xs text-gray-500">
                  <StarIcon className="w-3 h-3 text-yellow-400 mr-1" />
                  {(Number(course.rating) || 4.8).toFixed(1)} 评分
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={() => navigate(`/courses/${course.id}`)}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-white rounded-lg transition-colors"
                        title="预览课程"
                    >
                        <EyeIcon className="w-4 h-4" />
                    </button>
                    <button 
                        onClick={() => navigate(`/teacher/edit-course/${course.id}`)}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-white rounded-lg transition-colors"
                        title="编辑课程"
                    >
                        <PencilIcon className="w-4 h-4" />
                    </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
           <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">课程信息</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">价格</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">数据</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {courses.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                        <div>
                            <div className="text-sm font-bold text-gray-900">{course.title}</div>
                            <div className="text-xs text-gray-500 mt-1">{course.subject} • {course.duration}课时</div>
                        </div>
                    </td>
                    <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            course.status === 'approved' ? 'bg-green-50 text-green-700 border-green-100' :
                            course.status === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                            'bg-red-50 text-red-700 border-red-100'
                        }`}>
                            {course.status === 'approved' ? '已发布' : course.status === 'pending' ? '审核中' : '已拒绝'}
                        </span>
                    </td>
                    <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">¥{getCoursePrice(course)}</div>
                    </td>
                    <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-500 gap-4">
                            <span className="flex items-center"><UsersIcon className="w-4 h-4 mr-1"/> {course.studentCount || 0}</span>
                            <span className="flex items-center"><StarIcon className="w-4 h-4 mr-1 text-yellow-400"/> {(Number(course.rating) || 4.8).toFixed(1)}</span>
                        </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                        <button onClick={() => navigate(`/teacher/edit-course/${course.id}`)} className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">编辑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
           </table>
        </div>
      )}
    </div>
  )
}

export default TeacherCourses
